/* Shared feed item row component for the demo app.
   Renders a thumbnail, title, source, tags, and configurable action buttons. */

import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Sparkles } from "lucide-react";
import type { DemoItem } from "./demoData";
import type { LucideIcon } from "lucide-react";

export interface DemoAction {
  icon: LucideIcon;
  onClick: () => void;
  label: string;
}

interface DemoFeedItemRowProps {
  item: DemoItem;
  actions: DemoAction[];
  hideBorder?: boolean;
}

export function DemoFeedItemRow({
  item,
  actions,
  hideBorder,
}: DemoFeedItemRowProps) {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      className="group w-full md:max-w-[896px] flex flex-col px-4 md:px-6 outline-none rounded-sm"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="flex items-center gap-2.5 py-3">
        <div
          className="relative shrink-0 h-[68px] cursor-pointer"
          style={{ aspectRatio: "122 / 68" }}
        >
          <img
            src={item.thumbnail}
            alt={item.title}
            className="w-full h-full rounded-sm object-cover bg-muted"
            loading="lazy"
          />
          <span className="absolute bottom-1 right-1 bg-black/80 text-white text-[10px] font-medium leading-none px-1 py-0.5 rounded">
            {item.duration}
          </span>
        </div>

        <div className="flex-1 min-w-0 flex flex-col justify-between h-[68px]">
          <p className="text-sm font-medium leading-tight truncate max-w-full cursor-pointer hover:underline text-heading-foreground">
            {item.title}
          </p>
          <div className="flex items-center gap-2 min-w-0 max-w-full">
            <span className="text-xs text-muted-foreground shrink-0">
              {item.source}
            </span>
            <span className="text-xs text-muted-foreground shrink-0">·</span>
            <span className="text-xs text-muted-foreground shrink-0">
              {item.timeAgo}
            </span>
            {item.forYouReason && (
              <>
                <span className="text-xs text-muted-foreground shrink-0">·</span>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="inline-flex items-center gap-1 text-[11px] bg-primary/10 text-primary rounded-full px-2 py-0.5 shrink-0 cursor-default">
                        <Sparkles className="size-3" />
                        {item.forYouReason}
                      </span>
                    </TooltipTrigger>
                    {item.forYouReasonDetail && (
                      <TooltipContent>{item.forYouReasonDetail}</TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>
              </>
            )}
          </div>
          <div className="flex items-center gap-[11px]">
            {item.tags.map((tag) => (
              <span
                key={tag}
                className="text-xs text-muted-foreground cursor-pointer hover:text-foreground transition-colors"
              >
                #{tag}
              </span>
            ))}
          </div>
        </div>

        <div
          className={`shrink-0 flex items-end gap-1 h-[68px] transition-opacity duration-200 ${
            isHovered ? "opacity-100" : "opacity-0"
          }`}
        >
          {actions.map((action) => (
            <Button
              key={action.label}
              variant="ghost"
              size="icon-sm"
              className="bg-muted active:scale-95"
              onClick={action.onClick}
              aria-label={action.label}
            >
              <action.icon className="size-3.5" />
            </Button>
          ))}
        </div>
      </div>
      {!hideBorder && <div className="border-b border-border/50" />}
    </div>
  );
}
